/**
 * Safe localStorage wrapper for iOS private browsing and other edge cases
 *
 * In iOS Safari private browsing mode, localStorage is defined but throws errors.
 * This utility provides safe access with graceful fallback.
 */

const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Cache the check result
let localStorageAvailable = null;

const checkAvailability = () => {
  if (localStorageAvailable === null) {
    localStorageAvailable = isLocalStorageAvailable();
  }
  return localStorageAvailable;
};

/**
 * Safely get item from localStorage
 * @param {string} key - The localStorage key
 * @returns {string|null} The value or null if unavailable
 */
export const safeGetItem = (key) => {
  try {
    if (!checkAvailability()) return null;
    return window.localStorage.getItem(key);
  } catch (e) {
    console.warn(`[safeLocalStorage] Failed to get "${key}":`, e.message);
    return null;
  }
};

/**
 * Safely set item in localStorage
 * @param {string} key - The localStorage key
 * @param {string} value - The value to store
 * @returns {boolean} True if successful
 */
export const safeSetItem = (key, value) => {
  try {
    if (!checkAvailability()) return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`[safeLocalStorage] Failed to set "${key}":`, e.message);
    return false;
  }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} True if successful
 */
export const safeRemoveItem = (key) => {
  try {
    if (!checkAvailability()) return false;
    window.localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn(`[safeLocalStorage] Failed to remove "${key}":`, e.message);
    return false;
  }
};

/**
 * Safely clear all localStorage
 * @returns {boolean} True if successful
 */
export const safeClear = () => {
  try {
    if (!checkAvailability()) return false;
    window.localStorage.clear();
    return true;
  } catch (e) {
    console.warn('[safeLocalStorage] Failed to clear:', e.message);
    return false;
  }
};

/**
 * Get and parse JSON from localStorage
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value if parsing fails
 * @returns {*} Parsed value or default
 */
export const safeGetJSON = (key, defaultValue = null) => {
  try {
    const item = safeGetItem(key);
    if (!item) return defaultValue;
    return JSON.parse(item);
  } catch (e) {
    console.warn(`[safeLocalStorage] Failed to parse JSON for "${key}":`, e.message);
    return defaultValue;
  }
};

/**
 * Stringify and set JSON to localStorage
 * @param {string} key - The localStorage key
 * @param {*} value - The value to stringify and store
 * @returns {boolean} True if successful
 */
export const safeSetJSON = (key, value) => {
  try {
    const json = JSON.stringify(value);
    return safeSetItem(key, json);
  } catch (e) {
    console.warn(`[safeLocalStorage] Failed to stringify JSON for "${key}":`, e.message);
    return false;
  }
};
